/**
 * Erweiterte Filter-Komponente für Transaktionsdaten
 * Implementiert Jahr-, Typ-, Wertpapier- und Portfolio-Filter mit verbesserter UI
 */

import React, { useState, useEffect } from 'react';
import {
  SpaceBetween,
  Select,
  Button,
  Box,
  FormField,
  Container,
  ColumnLayout,
  ExpandableSection,
  Icon,
  TokenGroup
} from '@cloudscape-design/components';

import { TransaktionsDaten } from '@/types';

const TYPE_LABELS: Record<string, string> = {
  buy: 'Kauf',
  sell: 'Verkauf',
  transfer_in: 'Transfer-In',
  transfer_out: 'Transfer-Out',
  dividend: 'Dividende',
  interest: 'Zinsen'
};

export interface FilterOptions {
  jahr: string;
  typ: string;
  wertpapier: string;
  portfolio: string;
}

interface AdvancedFiltersProps {
  transaktionsDaten: TransaktionsDaten[];
  onFilterChange: (filters: FilterOptions) => void;
  activeFilters: FilterOptions;
}

/**
 * Komponente für erweiterte Filterung von Transaktionsdaten
 */
const AdvancedFilters: React.FC<AdvancedFiltersProps> = ({
  transaktionsDaten,
  onFilterChange,
  activeFilters
}) => {
  const [filter, setFilter] = useState<FilterOptions>(activeFilters);

  // Einzigartige Werte für Filter-Optionen extrahieren
  const filterOptionen = React.useMemo(() => {
    const jahre = [...new Set(transaktionsDaten.map(t => new Date(t.date).getFullYear().toString()))].sort();
    const vorhandeneTypen = [...new Set(transaktionsDaten.map(t => t.type))].sort();
    const wertpapiere = [...new Set(transaktionsDaten.map(t => t.holdname || t.name).filter(Boolean))].sort();
    const portfolios = [...new Set(transaktionsDaten.map(t => t.portfolio).filter(Boolean))].sort();

    return {
      jahre: [{ value: '', label: 'Alle Jahre' }, ...jahre.map(j => ({ value: j, label: j }))],
      typen: [
        { value: '', label: 'Alle Typen' },
        ...vorhandeneTypen.map(t => ({ value: t, label: TYPE_LABELS[t] ?? t }))
      ],
      wertpapiere: [
        { value: '', label: 'Alle Wertpapiere' },
        ...wertpapiere.map(w => ({ value: w, label: w }))
      ],
      portfolio: [
        { value: '', label: 'Alle Portfolios' },
        ...portfolios.map(p => ({ value: p, label: p }))
      ]
    };
  }, [transaktionsDaten]);

  /**
   * Handler für Filter-Änderungen
   */
  const handleFilterChange = (filterType: keyof FilterOptions, value: string) => {
    const neueFilter = { ...filter, [filterType]: value };
    setFilter(neueFilter);
    onFilterChange(neueFilter);
  };

  /**
   * Alle Filter zurücksetzen
   */
  const handleFilterReset = () => {
    const leereFilter: FilterOptions = {
      jahr: '',
      typ: '',
      wertpapier: '',
      portfolio: ''
    };
    setFilter(leereFilter);
    onFilterChange(leereFilter);
  };

  /**
   * Zählt aktive Filter
   */
  const aktiveFilterAnzahl = Object.values(filter).filter(v => v !== '').length;

  // Filter synchronisieren wenn von außen geändert
  useEffect(() => {
    setFilter(activeFilters);
  }, [activeFilters]);

  // Erstelle Token für aktive Filter
  const activeFilterTokens = React.useMemo(() => {
    const tokens: Array<{ label: string; dismissLabel: string; value: string; key: string }> = [];
    
    if (filter.jahr) {
      tokens.push({
        label: `Jahr: ${filter.jahr}`,
        dismissLabel: `Jahr ${filter.jahr} entfernen`,
        value: filter.jahr,
        key: 'jahr'
      });
    }
    if (filter.typ) {
      const typLabel = filterOptionen.typen.find(t => t.value === filter.typ)?.label || filter.typ;
      tokens.push({
        label: `Typ: ${typLabel}`,
        dismissLabel: `Typ ${typLabel} entfernen`,
        value: filter.typ,
        key: 'typ'
      });
    }
    if (filter.wertpapier) {
      tokens.push({
        label: `Wertpapier: ${filter.wertpapier}`,
        dismissLabel: `Wertpapier ${filter.wertpapier} entfernen`,
        value: filter.wertpapier,
        key: 'wertpapier'
      });
    }
    if (filter.portfolio) {
      tokens.push({
        label: `Portfolio: ${filter.portfolio}`,
        dismissLabel: `Portfolio ${filter.portfolio} entfernen`,
        value: filter.portfolio,
        key: 'portfolio'
      });
    }
    
    return tokens;
  }, [filter, filterOptionen]);

  const handleTokenDismiss = (tokenKey: string) => {
    const neueFilter = { ...filter, [tokenKey]: '' };
    setFilter(neueFilter);
    onFilterChange(neueFilter);
  };

  return (
    <SpaceBetween direction="vertical" size="l">
      {/* Aktive Filter Chips */}
      {activeFilterTokens.length > 0 && (
        <Container>
          <SpaceBetween direction="vertical" size="s">
            <Box variant="h4">
              <Icon name="filter" variant="subtle" /> 
              Aktive Filter ({activeFilterTokens.length})
            </Box>
            <TokenGroup
              onDismiss={({ detail }) => handleTokenDismiss(activeFilterTokens[detail.itemIndex].key)}
              items={activeFilterTokens.map((token) => ({
                label: token.label,
                dismissLabel: token.dismissLabel
              }))}
              alignment="horizontal"
            />
            <SpaceBetween direction="horizontal" size="s">
              <Button
                variant="link"
                onClick={handleFilterReset}
                iconName="close"
              >
                Alle Filter löschen
              </Button>
            </SpaceBetween>
          </SpaceBetween>
        </Container>
      )}

      {/* Filter-Auswahl */}
      <ExpandableSection
        headerText="Erweiterte Filter"
        headerCounter={aktiveFilterAnzahl > 0 ? `(${aktiveFilterAnzahl})` : ''}
        defaultExpanded={aktiveFilterAnzahl === 0}
        variant="container"
      >
        <Container>
          <SpaceBetween direction="vertical" size="l">
            {/* Detaillierte Filter */}
            <Box>
              <Box variant="h4" padding={{ bottom: "s" }}>
                <Icon name="filter" variant="subtle" /> 
                Detailfilter
              </Box>
              <ColumnLayout columns={2} variant="text-grid">
                <FormField 
                  label="Jahr" 
                  description="Transaktionen nach Jahr filtern"
                  stretch
                >
                  <Select
                    selectedOption={filterOptionen.jahre.find(opt => opt.value === filter.jahr) || null}
                    onChange={({ detail }) => handleFilterChange('jahr', detail.selectedOption?.value || '')}
                    options={filterOptionen.jahre}
                    placeholder="Alle Jahre"
                    expandToViewport
                  />
                </FormField>

                <FormField 
                  label="Transaktionstyp" 
                  description="Nach Transaktionstyp filtern"
                  stretch
                >
                  <Select
                    selectedOption={filterOptionen.typen.find(opt => opt.value === filter.typ) || null}
                    onChange={({ detail }) => handleFilterChange('typ', detail.selectedOption?.value || '')}
                    options={filterOptionen.typen}
                    placeholder="Alle Typen"
                    expandToViewport
                  />
                </FormField>

                <FormField 
                  label="Wertpapier" 
                  description="Spezifisches Wertpapier auswählen"
                  stretch
                >
                  <Select
                    selectedOption={filterOptionen.wertpapiere.find(opt => opt.value === filter.wertpapier) || null}
                    onChange={({ detail }) => handleFilterChange('wertpapier', detail.selectedOption?.value || '')}
                    options={filterOptionen.wertpapiere}
                    placeholder="Alle Wertpapiere"
                    expandToViewport
                    filteringType="auto"
                  />
                </FormField>

                <FormField 
                  label="Portfolio" 
                  description="Nach Portfolio filtern"
                  stretch
                >
                  <Select
                    selectedOption={filterOptionen.portfolio.find(opt => opt.value === filter.portfolio) || null}
                    onChange={({ detail }) => handleFilterChange('portfolio', detail.selectedOption?.value || '')}
                    options={filterOptionen.portfolio}
                    placeholder="Alle Portfolios"
                    expandToViewport
                  />
                </FormField>
              </ColumnLayout>
            </Box>


          </SpaceBetween>
        </Container>
      </ExpandableSection>
    </SpaceBetween>
  );
};

export default AdvancedFilters;